import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * SEO Component for managing meta tags and structured data
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.canonical - Canonical URL
 * @param {string} props.image - Open Graph image URL
 * @param {string} props.type - Open Graph type (website, article, etc.)
 * @param {Object|Array} props.schema - JSON-LD structured data
 */
const SEO = ({
  title = "Opera Today - Opera, Festival, and Classical Events",
  description = "Discover upcoming opera, festival, and classical music events worldwide.",
  canonical = "https://www.operatoday.org/",
  image = "https://www.operatoday.org/images/Teatro_alla_Scala.jpg",
  type = "website",
  schema = null,
}) => {
  // Ensure canonical URL is absolute
  const absoluteCanonical = canonical.startsWith("http")
    ? canonical
    : `https://www.operatoday.org${
        canonical.startsWith("/") ? canonical : `/${canonical}`
      }`;

  // Ensure image URL is absolute
  const absoluteImage = image?.startsWith("http")
    ? image
    : `https://www.operatoday.org${
        image?.startsWith("/") ? image : `/${image}`
      }`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={absoluteCanonical} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={absoluteCanonical} />
      {absoluteImage && <meta property="og:image" content={absoluteImage} />}
      <meta property="og:site_name" content="Opera Today" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {absoluteImage && <meta name="twitter:image" content={absoluteImage} />}

      {/* Structured Data (JSON-LD) */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
